/**
 * Control Card Feature - Main Entry Point
 * 
 * Public API for control card generation.
 * Simple, clean interface for generating professional control card PDFs.
 */

import { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from '../../lib/supabase/client'
import {
  GenerateControlCardUseCase,
  type GenerateControlCardRequest,
  type GenerateControlCardResponse,
  type SimpleControlCardRequest,
  type ControlCardOptions,
  getGenerateControlCardUseCase,
  ControlCardContainer,
} from './application'
import { resolveNdtDateOverride, withNdtDateOverride } from './application/ndtDateOverride'
import type { ControlCardData } from './infrastructure'

// Re-export types for external consumers
export type {
  GenerateControlCardRequest,
  GenerateControlCardResponse,
  SimpleControlCardRequest,
  ControlCardOptions,
}

// Re-export all layers for advanced usage
export * from './config'
export * from './domain'
export * from './infrastructure'
export * from './presentation'
export * from './application'

/**
 * Generate a control card PDF
 * 
 * @example
 * ```typescript
 * const result = await generateControlCard({
 *   equipmentId: 'uuid-123',
 *   inspection: {
 *     jobType: 'Periodisk kontroll',
 *     performedDate: new Date().toISOString(),
 *     performedBy: 'John Doe',
 *     responsiblePerson: 'Jane Smith',
 *     status: 'OK',
 *   },
 * })
 * 
 * if (result.success) {
 *   const blob = result.blob
 *   // Upload or download
 * }
 * ```
 */
export async function generateControlCard(
  request: GenerateControlCardRequest
): Promise<GenerateControlCardResponse> {
  const useCase = getGenerateControlCardUseCase()
  return useCase.execute(request)
}

/**
 * Generate a control card PDF with simplified parameters
 * 
 * @example
 * ```typescript
 * const result = await generateControlCardSimple({
 *   equipmentId: 'uuid-123',
 *   jobType: 'Periodisk kontroll',
 *   performedBy: 'John Doe',
 *   responsiblePerson: 'Jane Smith',
 * })
 * ```
 */
export async function generateControlCardSimple(
  request: SimpleControlCardRequest,
  options?: ControlCardOptions
): Promise<GenerateControlCardResponse> {
  const fullRequest: GenerateControlCardRequest = {
    equipmentId: request.equipmentId,
    inspection: {
      jobType: request.jobType,
      performedDate: request.performedDate || new Date().toISOString(),
      performedBy: request.performedBy,
      responsiblePerson: request.responsiblePerson,
      status: request.status || 'OK',
      remarks: request.remarks,
      performedBySignature: request.performedBySignature,
      responsiblePersonSignature: request.responsiblePersonSignature,
    },
    options,
  }

  return generateControlCard(fullRequest)
}

/**
 * Preview a control card (with watermark, no report number assignment)
 * 
 * @example
 * ```typescript
 * const result = await previewControlCard({
 *   equipmentId: 'uuid-123',
 *   inspection: { ... },
 *   options: { attachmentImage: 'base64...' }
 * })
 * ```
 */
export async function previewControlCard(
  request: GenerateControlCardRequest
): Promise<GenerateControlCardResponse> {
  const useCase = getGenerateControlCardUseCase()
  return useCase.preview(request)
}

/**
 * Render the Prototype control-card template. Shared implementation for both
 * `previewPrototypeControlCard` (watermarked, no report-number assignment)
 * and `finalizePrototypeControlCard` (clean, assigns a report number when the
 * request doesn't carry one).
 */
async function runPrototypeControlCard(
  request: GenerateControlCardRequest,
  finalize: boolean,
): Promise<GenerateControlCardResponse> {
  try {
    const { PrototypeControlCardTemplate } = await import('./presentation')
    const container = getContainer()
    const repo = container.createRepository()

    const eqResult = await repo.getCompleteControlCardData(request.equipmentId)
    if (!eqResult.success) {
      return { success: false, error: `Failed to fetch equipment: ${eqResult.error}` }
    }

    const includeSubEquipment = request.options?.includeSubEquipment !== false
    const ndtDateOverride = resolveNdtDateOverride(request)

    const controlCardData: ControlCardData = {
      mainEquipment: withNdtDateOverride(eqResult.data.mainEquipment, ndtDateOverride),
      subEquipment: includeSubEquipment
        ? eqResult.data.subEquipment.map(eq => withNdtDateOverride(eq, ndtDateOverride))
        : [],
      inspection: request.inspection,
    }

    const template = new PrototypeControlCardTemplate()
    await template.renderComplete(controlCardData, request.logos, {
      finalize,
      inspectionId: request.options?.inspectionId,
      attachmentImage: request.options?.attachmentImage,
    })

    return {
      success: true,
      blob: template.getBlob(),
      dataUrl: template.getDataUrl(),
      reportNumber: controlCardData.inspection.reportNumber || 'N/A',
      metadata: {
        equipmentName: controlCardData.mainEquipment.name || 'Unknown',
        subEquipmentCount: controlCardData.subEquipment.length,
        specificationCount: controlCardData.mainEquipment.specifications.count(),
        generatedAt: new Date(),
      },
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

/**
 * Preview a control card using the Prototype template (HTML-style layout).
 * Same data flow as previewControlCard but renders with PrototypeControlCardTemplate.
 */
export async function previewPrototypeControlCard(
  request: GenerateControlCardRequest
): Promise<GenerateControlCardResponse> {
  return runPrototypeControlCard(request, false)
}

/**
 * Finalize a control card using the Prototype template (no watermark; assigns
 * a report number when `request.inspection.reportNumber` is absent). Used by
 * the publish flow so the produced PDF matches the Prototype preview the user
 * just looked at.
 */
export async function finalizePrototypeControlCard(
  request: GenerateControlCardRequest
): Promise<GenerateControlCardResponse> {
  return runPrototypeControlCard(request, true)
}

/**
 * Finalize a control card (assigns report number, no watermark)
 * 
 * @example
 * ```typescript
 * const result = await finalizeControlCard({
 *   equipmentId: 'uuid-123',
 *   inspection: { ... },
 *   options: { inspectionId: 'uuid-456' },
 * })
 * 
 * console.log('Report number:', result.reportNumber)
 * ```
 */
export async function finalizeControlCard(
  request: GenerateControlCardRequest
): Promise<GenerateControlCardResponse> {
  const useCase = getGenerateControlCardUseCase()
  return useCase.finalize(request)
}

/**
 * Generate and save control card to file
 * 
 * @example
 * ```typescript
 * await saveControlCard(
 *   {
 *     equipmentId: 'uuid-123',
 *     inspection: { ... },
 *   },
 *   'control-card.pdf'
 * )
 * ```
 */
export async function saveControlCard(
  request: GenerateControlCardRequest,
  filename: string
): Promise<GenerateControlCardResponse> {
  const useCase = getGenerateControlCardUseCase()
  return useCase.generateAndSave(request, filename)
}

/**
 * Create a custom use case instance with specific Supabase client
 * Useful for testing or multi-tenant scenarios
 * 
 * @example
 * ```typescript
 * const customUseCase = createControlCardUseCase(customSupabaseClient)
 * const result = await customUseCase.execute(request)
 * ```
 */
export function createControlCardUseCase(
  supabaseClient: SupabaseClient = supabase
): GenerateControlCardUseCase {
  return new GenerateControlCardUseCase(supabaseClient)
}

/**
 * Get the dependency injection container
 * For advanced usage and testing
 */
export function getContainer(): ControlCardContainer {
  return ControlCardContainer.getInstance()
}

/**
 * Clear all caches (useful for testing or manual refresh)
 */
export function clearControlCardCaches(): void {
  const container = ControlCardContainer.getInstance()
  const useCase = container.getGenerateControlCardUseCase()
  useCase.clearCaches()
}

/**
 * Get cache statistics
 */
export function getControlCardCacheStats(): {
  templates: number
  references: { manufacturers: number; standards: number; total: number }
} {
  const useCase = getGenerateControlCardUseCase()
  return useCase.getCacheStats()
}
